import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import React from 'react';

import { DisableInProduction } from '@/components/atoms/DisableInProduction';
import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { Currency } from '@/data/currencies/type';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { LocaleContext, useTranslator } from '@/hooks/useTranslator';
import { AccountDetailsModal } from '@/Screens/Dashboard/Home/AccountDetailsModal';
import { DetailedBalance } from '@/types/bankInfo';
import { CurrencyCodeType } from '@/types/currency';
import { Locale } from '@/types/locale';

interface Props {
  readonly balance: DetailedBalance;
}

export const BalanceWidget: React.FC<Props> = (props: Props): React.ReactElement => {
  const [isDetailsOpen, setIsDetailsOpen] = React.useState<boolean>(false);
  const { balance } = props;

  const formatter = useCurrencyFormatter();
  const formattedBalance = React.useMemo(
    (): string => formatter.format(balance.available, balance.currencyCode),
    [balance.currencyCode, balance.available, formatter],
  );
  const locale = React.useContext<Locale>(LocaleContext);
  const tr = useTranslator();
  const currency = React.useMemo(
    (): Currency => Currency.fromCode(locale, balance.currencyCode),
    [balance.currencyCode, locale],
  );

  const Icon = React.useMemo(
    (): React.FunctionComponent<React.SVGProps<SVGSVGElement>> => currency.icon,
    [currency.icon],
  );

  const onShowDetails = React.useCallback((): void => {
    setIsDetailsOpen(true);
  }, []);

  const onCloseDetails = React.useCallback((): void => {
    setIsDetailsOpen(false);
  }, []);

  const modalDescription = React.useMemo(
    (): React.ReactElement => (
      <Box display="flex" alignItems="center" gap={1}>
        <currency.icon width={24} />
        <Typography variant="body2" color="text.primary">
          {currency.name}
        </Typography>
      </Box>
    ),
    [currency],
  );

  return (
    <Box
      display="flex"
      alignItems="flex-end"
      justifyContent="space-between"
      bgcolor="custom.Primary1Background"
      borderRadius={2}
      padding={3}>
      <Box display="flex" alignItems="center" gap={3}>
        <Icon width={32} height="100%" />
        <Box display="flex" flexDirection="column" gap={0.5}>
          <Typography variant="body1" color="custom.LightGray">
            {tr('BALANCE')}
          </Typography>
          <Typography fontSize={24} fontWeight={400} lineHeight="120%" letterSpacing="-1%">
            {formattedBalance}
          </Typography>
        </Box>
      </Box>

      <DisableInProduction when={currency.code !== CurrencyCodeType.USDC}>
        <Button variant="light" size="compact" onClick={onShowDetails}>
          {tr('SEE_DETAILS')}
        </Button>
      </DisableInProduction>

      <Dialog open={isDetailsOpen} onClose={onCloseDetails}>
        <ApplicationModal
          title={tr('ACCOUNT_DETAILS')}
          description={modalDescription}
          onClose={onCloseDetails}>
          <AccountDetailsModal currency={currency.code} />
        </ApplicationModal>
      </Dialog>
    </Box>
  );
};
